import React from 'react';

import AuthStore from '@app/src/common/store/AuthStore';
import AppStore from '@app/src/common/store/AppStore';

import Home from '@app/src/common/components/home/Home';
import AccessDenied from '@app/src/common/components/access-denied/AccessDenied';
import AuthPage from '@app/src/components/authentication/auth-page/AuthPage';
import ForgotPasswordPage from '@app/src/components/authentication/forgot-password-page/ForgotPasswordPage';
import ResetPasswordPage from '@app/src/components/authentication/reset-password-page/ResetPasswordPage';
import ServerErrorPage from '@app/src/components/server-error-page/ServerErrorPage';
import LogOutPage from '@app/src/components/authentication/auth-page/LogOutPage';
import NotFound from '@app/src/common/components/not-found/NotFound';
import ConsentPage from '@app/src/common/components/consent/ConsentPage';

const UserManagementHeader = React.lazy(
  () => import('@app/src/components/user-management/Header')
);
const UserManagement = React.lazy(
  () => import('@app/src/components/user-management/UserManagement')
);

const DeviceManagementHeader = React.lazy(
  () => import('@app/src/components/device-management/Header')
);
const DeviceManagement = React.lazy(
  () => import('@app/src/components/device-management/DeviceManagement')
);

const EventManagementHeader = React.lazy(
  () => import('@app/src/components/event-management/Header')
);
const EventManagement = React.lazy(
  () => import('@app/src/components/event-management/EventManagement')
);

const UserProfileHeader = React.lazy(
  () => import('@app/src/components/authentication/profile-page/Header')
);
const UserProfile = React.lazy(
  () => import('@app/src/components/authentication/profile-page/UserProfile')
);

const ContentManagementHeader = React.lazy(
  () => import('@app/src/components/content-management/Header')
);
const ContentManagement = React.lazy(
  () => import('@app/src/components/content-management/ContentManagement')
);

const ScheduleHeader = React.lazy(
  () => import('@app/src/components/schedule/Header')
);
const Schedule = React.lazy(
  () => import('@app/src/components/schedule/SchedulePage')
);

const FileStorageHeader = React.lazy(
  () => import('@app/src/components/file-storage/Header')
);
const FileStorage = React.lazy(
  () => import('@app/src/components/file-storage/FileStorage')
);

const OrderManagementHeader = React.lazy(
  () => import('@app/src/components/order-management/Header')
);
const OrderManagement = React.lazy(
  () => import('@app/src/components/order-management/OrderManagement')
);

const WayFindingHeader = React.lazy(
  () => import('@app/src/components/way-finding/Header')
);
const WayFinding = React.lazy(
  () => import('@app/src/components/way-finding/WayFinding')
);

const BookingManagementHeader = React.lazy(
  () => import('@app/src/components/booking-management/Header')
);
const BookingManagement = React.lazy(
  () => import('@app/src/components/booking-management/BookingManagement')
);

const CustomSolutionsHeader = React.lazy(
  () => import('@app/src/components/custom-solutions/Header')
);
const CustomSolutions = React.lazy(
  () => import('@app/src/components/custom-solutions/CustomSolutions')
);

const ParkingManagementHeader = React.lazy(
  () => import('@app/src/components/parking-management/Header')
);
const ParkingManagement = React.lazy(
  () => import('@app/src/components/parking-management/ParkingManagement')
);

const InventoryManagementHeader = React.lazy(
  () => import('@app/src/components/inventory-management/Header')
);
const InventoryManagement = React.lazy(
  () => import('@app/src/components/inventory-management/InventoryManagement')
);

const FormManagementHeader = React.lazy(
  () => import('@app/src/components/form-management/Header')
);
const FormManagement = React.lazy(
  () => import('@app/src/components/form-management/FormManagement')
);

export interface IRouteOptions {
  path: string;
  name: string;
  header?: React.LazyExoticComponent<any>;
  component?: React.LazyExoticComponent<any> | React.ComponentType<any>;
  navigationRoute: boolean;
}

export interface IRoutes {
  [key: string]: IRouteOptions;
}

export interface IPageRoute {
  path: string;
  name: string;
  component: React.ComponentType<any>;
  pathName?: (id: string, secondId?: string) => string;
  permissionCheck?: () => boolean;
  childRoute?: boolean;
  exact?: boolean;
}
export interface IPageRoutes {
  [key: string]: IPageRoute;
}

export const guidRegex =
  '([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})';

export const AUTH_ROUTES: IRoutes = {
  userManagement: {
    path: '/user-management',
    name: 'User Management',
    header: UserManagementHeader,
    component: UserManagement,
    navigationRoute: true
  },
  deviceManagement: {
    path: '/device-management',
    name: 'Device Management',
    header: DeviceManagementHeader,
    component: DeviceManagement,
    navigationRoute: true
  },
  eventManagement: {
    path: '/event-management',
    name: 'Event Management',
    header: EventManagementHeader,
    component: EventManagement,
    navigationRoute: true
  },
  profilePage: {
    path: '/profile',
    name: 'My Profile',
    header: UserProfileHeader,
    component: UserProfile,
    navigationRoute: false
  },
  contentManagement: {
    path: '/content-management',
    name: 'Content',
    header: ContentManagementHeader,
    component: ContentManagement,
    navigationRoute: true
  },
  schedule: {
    path: '/schedule',
    name: 'Schedule',
    header: ScheduleHeader,
    component: Schedule,
    navigationRoute: true
  },
  fileStorage: {
    path: '/file-storage',
    name: 'File Storage',
    header: FileStorageHeader,
    component: FileStorage,
    navigationRoute: true
  },
  orderManagement: {
    path: '/order-management',
    name: 'Order',
    header: OrderManagementHeader,
    component: OrderManagement,
    navigationRoute: true
  },
  wayFinding: {
    path: '/way-finding',
    name: 'Way Finding',
    header: WayFindingHeader,
    component: WayFinding,
    navigationRoute: true
  },
  bookingManagement: {
    path: '/booking-management',
    name: 'Booking',
    header: BookingManagementHeader,
    component: BookingManagement,
    navigationRoute: true
  },
  customSolutions: {
    path: '/custom',
    name: 'Custom Solutions',
    header: CustomSolutionsHeader,
    component: CustomSolutions,
    navigationRoute: true
  },
  parkingManagement: {
    path: '/parking',
    name: 'Parking Management',
    header: ParkingManagementHeader,
    component: ParkingManagement,
    navigationRoute: true
  },
  inventoryManagement: {
    path: '/inventory',
    name: 'Inventory Management',
    header: InventoryManagementHeader,
    component: InventoryManagement,
    navigationRoute: true
  },
  formManagement: {
    path: '/form-management',
    name: 'Form Management',
    header: FormManagementHeader,
    component: FormManagement,
    navigationRoute: true
  }
};

export const PUBLIC_ROUTES: IRoutes = {
  homePage: {
    path: '/',
    name: 'Home',
    component: Home,
    navigationRoute: false
  },
  notAuthorizedPage: {
    path: '/not-authorized',
    name: 'Not Authorized',
    component: AccessDenied,
    navigationRoute: false
  },
  serverErrorPage: {
    path: '/server-error',
    name: 'Server Error',
    component: ServerErrorPage,
    navigationRoute: false
  },
  notFoundPage: {
    path: '/404',
    name: 'Not Found',
    component: NotFound,
    navigationRoute: false
  },
  authPage: {
    path: '/auth',
    name: 'Auth Page',
    component: AuthPage,
    navigationRoute: false
  },
  forgotPasswordPage: {
    path: '/forgot-password',
    name: 'Forgot Password Page',
    component: ForgotPasswordPage,
    navigationRoute: false
  },
  resetPasswordPage: {
    path: '/reset-password',
    name: 'Reset Password Page',
    component: ResetPasswordPage,
    navigationRoute: false
  },
  logOutPage: {
    path: '/logout',
    name: 'LogOut Page',
    component: LogOutPage,
    navigationRoute: false
  },
  consentPage: {
    path: '/consent',
    name: 'Consent',
    component: ConsentPage,
    navigationRoute: false
  }
};

const ROUTES: IRoutes = {
  ...PUBLIC_ROUTES,
  ...AUTH_ROUTES
};

export async function getRoutes(): Promise<IRouteOptions[]> {
  const routes: IRouteOptions[] = [];
  const access = await AuthStore.checkAccess();

  if (access && access.token) {
    await AppStore.loadCustomSolutionSetting();

    if (AuthStore.isDeviceInspector) {
      routes.push(ROUTES.deviceManagement);
    }
    if (AuthStore.isContentViewer) {
      routes.push(ROUTES.contentManagement);
    }
    if (AuthStore.isScheduleViewUser) {
      routes.push(ROUTES.schedule);
    }
    if (AuthStore.isStorageViewer) {
      routes.push(ROUTES.fileStorage);
    }
    if (AuthStore.isCompanyAdmin || AuthStore.isUserGroupViewer) {
      routes.push(ROUTES.userManagement);
    }
    if (AuthStore.isEventManager) {
      routes.push(ROUTES.eventManagement);
    }
    if (AuthStore.isFormManager) {
      routes.push(ROUTES.formManagement);
    }
    if (AuthStore.isParkingViewer) {
      routes.push(ROUTES.parkingManagement);
    }
    if (AuthStore.isWayFindingViewer) {
      routes.push(ROUTES.wayFinding);
    }
    if (AuthStore.isOrderViewer) {
      routes.push(ROUTES.orderManagement);
    }
    if (AuthStore.isBookingViewer) {
      routes.push(ROUTES.bookingManagement);
    }
    if (AuthStore.isInventoryViewer) {
      routes.push(ROUTES.inventoryManagement);
    }
    if (AuthStore.isCustomSolutionsViewer) {
      routes.push(ROUTES.customSolutions);
    }
    if (AuthStore.isCompanyUser) {
      routes.push(ROUTES.profilePage);
    }
  }

  return Promise.resolve(routes);
}

export default ROUTES;
